/** @shell standard @header bar */
import React, { useMemo, useCallback, useState } from 'react';
import { View, Text, StyleSheet, RefreshControl, Platform, Alert } from 'react-native';
import Animated from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SvgIcon } from '../components/common/SvgIcon';
import { MainLoader } from '../components/load-main/MainLoader';
import { Clickable } from '../components/common/Clickable';
import { useNavigation } from '@react-navigation/native';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import { createPageFooterOutlineCtaStyles } from '../shared/theme/styles/buttons';
import { useSubscriptionContext, usePendingSubscriptions, type SubscriptionData, filterFreezeToggleableDates } from '../data/domains/subscription';
import { useApiConfig, useOrganizationSettings } from '../data/domains/config';
import { fetchSubscriptionPaymentUrl } from '../data/domains/checkout';
import { resolveSubscriptionDisplayStatus } from './components/MySubscriptionCard';
import {
  mapPendingSubscriptionRawToData,
  getActiveMealDayProgressFromSubscription,
  getSubscriptionUpgradeDayLeft,
  useSubscriptionPlans,
  findCatalogPlanForSubscription,
  applyV3CatalogPlanDayOffFields,
  setSelectedSubscription,
} from '../data/domains/subscription';
import { isUsableCheckoutPlan } from '../data/domains/checkout';
import { useAuthStore } from '../shared/auth/store';
import { useTranslation } from '../shared/i18n';
import { ScreenHeader, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { androidHeaderTopInset, androidAppHeaderContentHeight } from '../shared/theme/sharedStyles';
import { ErrorState } from '../components/common/ErrorState';
import FreezeCalendarPopup from '../calendar/components/FreezeCalendarPopup';
import { useRequireVerified } from '../shared/auth/useRequireVerified';
import { useAuthNavigation, useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import {
  StackScreenShell,
  STACK_SCROLL_GUTTER,
  studioCardShell,
  androidScaleFont,
  androidScaleSize,
  androidFlatSurfaceStyle,
} from '../shared/theme/studio/stackScreenShell';
import { MySubscriptionCard } from './components/MySubscriptionCard';

/** Vertical padding above/below the footer CTA (excluding safe-area inset). */
const FOOTER_VERTICAL_PAD = appTheme.spacing.md;
/** Approximate tap target + inner padding of the outline footer button. */
const FOOTER_CTA_HEIGHT = androidScaleSize(52);

function footerTotalHeight(bottom_inset: number): number {
  return FOOTER_VERTICAL_PAD * 2 + FOOTER_CTA_HEIGHT + bottom_inset;
}

/** Build a checkout-ready catalog plan from an owned subscription card. */
function buildRenewCheckoutPlan(
  sub: SubscriptionData,
  catalog_plans: unknown[] | undefined,
): Record<string, unknown> | null {
  const catalog = findCatalogPlanForSubscription(sub, Array.isArray(catalog_plans) ? catalog_plans : []);
  const detail = (sub.detail ?? {}) as Record<string, unknown>;
  const sub_detail =
    sub.subscription_detail != null && typeof sub.subscription_detail === 'object'
      ? (sub.subscription_detail as Record<string, unknown>)
      : null;

  const base_raw = (catalog ?? {
    id: Number(sub_detail?.id ?? detail.subscription_id ?? (sub as { subscription_id?: number }).subscription_id ?? 0),
    subscription_id: Number(
      sub_detail?.id ?? detail.subscription_id ?? (sub as { subscription_id?: number }).subscription_id ?? 0,
    ),
    title: detail.title ?? sub.title,
    arabic_title: detail.arabic_title ?? detail.a_title,
    days: Number(detail.day ?? detail.days ?? 0) || 0,
    day: Number(detail.day ?? detail.days ?? 0) || 0,
    number_of_meals: Number(detail.total_meals ?? 0) || 0,
    number_of_snacks: Number(detail.total_snacks ?? 0) || 0,
    total_meals: Number(detail.total_meals ?? 0) || 0,
    total_snacks: Number(detail.total_snacks ?? 0) || 0,
    cost: Number(detail.cost ?? sub.cost ?? sub.price ?? 0) || 0,
    offer_cost: Number(detail.price ?? detail.cost ?? sub.price ?? sub.cost ?? 0) || 0,
    subscription_category_id: Number(
      detail.subscription_category_id ?? sub_detail?.subscription_category_id ?? (sub as { category_id?: number }).category_id ?? 0,
    ),
    category_id: Number(
      detail.category_id ?? detail.subscription_category_id ?? sub_detail?.category_id ?? 0,
    ),
    celebrity_id: Number(detail.celebrity_id ?? 0) || 0,
    no_meals_on_days: detail.day_off,
    max_day_off: detail.day_off,
  }) as Record<string, unknown>;

  const plan_base = applyV3CatalogPlanDayOffFields(base_raw);
  const days =
    Number(plan_base.days ?? plan_base.day ?? detail.day ?? detail.days ?? 0) || 0;
  const subscription_category_id =
    plan_base.subscription_category_id ??
    plan_base.category_id ??
    (plan_base.subscription_category as { id?: number } | undefined)?.id ??
    null;

  const plan_for_checkout = {
    ...plan_base,
    days,
    day: days,
    id: plan_base.id ?? plan_base.subscription_id,
    subscription_category_id,
    category_id: subscription_category_id ?? plan_base.category_id,
    subscription_id: plan_base.subscription_id ?? plan_base.id,
    celebrity_id: plan_base.celebrity_id ?? 0,
    number_of_meals: plan_base.number_of_meals ?? plan_base.total_meals ?? detail.total_meals ?? 0,
    number_of_snacks: plan_base.number_of_snacks ?? plan_base.total_snacks ?? detail.total_snacks ?? 0,
    customized_meals: plan_base.customized_meals ?? plan_base.is_customized ?? [],
    isCustomAllowed: plan_base.isCustomAllowed === true || plan_base.is_customized === true,
  };

  return isUsableCheckoutPlan(plan_for_checkout) ? plan_for_checkout : null;
}

function createMySubscriptionsStyles(c: ThemeColors) {
  const sf = androidScaleFont;
  const ss = androidScaleSize;

  return StyleSheet.create({
    shell: { flex: 1 },
    center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
    scroll: { flex: 1 },
    scrollContent: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      flexGrow: 1,
    },
    emptyState: { alignItems: 'center', paddingVertical: 48, paddingHorizontal: appTheme.spacing.md },
    emptyIconWrap: {
      width: ss(96),
      height: ss(96),
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: appTheme.spacing.lg,
      ...studioCardShell(c),
      ...androidFlatSurfaceStyle(),
      borderRadius: ss(48),
      backgroundColor: c.surfaceLight,
    },
    emptyTitle: { ...appTheme.typography.h3, fontSize: sf(appTheme.typography.h3.fontSize ?? 20), color: c.text, marginBottom: 8, textAlign: 'center' },
    emptySubtitle: {
      ...appTheme.typography.bodySmall,
      fontSize: sf(appTheme.typography.bodySmall.fontSize ?? 14),
      color: c.textSecondary,
      textAlign: 'center',
    },
    pageFooter: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: FOOTER_VERTICAL_PAD,
      paddingHorizontal: STACK_SCROLL_GUTTER,
      backgroundColor: c.surface,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: c.borderLight,
      ...Platform.select({
        ios: { ...appTheme.shadows.lg, shadowColor: c.shadowColor },
        android: androidFlatSurfaceStyle(),
      }),
    },
    pageFooterCtaWrap: {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
    },
  });
}

export const MySubscriptionsScreen: React.FC = () => {
  const navigation = useAuthNavigation();
  const on_back_exit = useExitOrGoBack();
  const insets = useSafeAreaInsets();
  useRequireVerified('MySubscriptions');
  const themeColors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createMySubscriptionsStyles(themeColors), [themeColors]);
  const footer_total_height = footerTotalHeight(insets.bottom);
  const content_top = androidHeaderTopInset(insets.top) + androidAppHeaderContentHeight() + appTheme.spacing.md;
  const { scrollY, onScroll } = useScreenHeaderScroll();
  const scroll_padding_bottom = footer_total_height + appTheme.spacing.lg;
  const { subscriptions, isLoading, error, refetch, availableDates, dayConfigMap, filterDatesBySubscription, config } = useSubscriptionContext();
  const { pendingPlan, cancelPending, refetch: refetch_pending } = usePendingSubscriptions();
  const { organization } = useApiConfig();
  const { data: catalog_plans } = useSubscriptionPlans();
  const user = useAuthStore(s => s.user);
  const { t } = useTranslation();
  const { settings } = useOrganizationSettings();
  const menu_selection_before = settings?.menu_selection_before ?? 3;
  const [refreshing, setRefreshing] = React.useState(false);
  const [freezePopupSub, setFreezePopupSub] = React.useState<any>(null);
  const [freezePopupMode, setFreezePopupMode] = React.useState<'freeze' | 'unfreeze'>('freeze');
  const [pending_action_id, set_pending_action_id] = useState<number | null>(null);

  const default_payment_gateway = useMemo(() => {
    const gateways = organization?.payment_gateway;
    if (!Array.isArray(gateways) || gateways.length === 0) {
      return '';
    }
    const first = gateways[0] as { name?: string };
    return typeof first.name === 'string' ? first.name : '';
  }, [organization?.payment_gateway]);

  const getMealProgress = useCallback(
    (sub: (typeof subscriptions)[number]) =>
      getActiveMealDayProgressFromSubscription(sub, availableDates, subscriptions, dayConfigMap, config),
    [availableDates, subscriptions, dayConfigMap, config],
  );

  const display_subscriptions = useMemo(() => {
    const list = [...subscriptions];
    if (pendingPlan != null && typeof user?.id === 'number') {
      const pending_id = Number((pendingPlan as { id?: number }).id);
      if (Number.isFinite(pending_id) && !list.some(s => s.id === pending_id)) {
        list.unshift(mapPendingSubscriptionRawToData(pendingPlan as Record<string, unknown>, user.id));
      }
    }
    return list;
  }, [subscriptions, pendingPlan, user?.id]);

  const goToSubscriptionList = useCallback(() => {
    navigation.navigate('SubscriptionList');
  }, [navigation]);

  const handleRenew = useCallback(
    async (sub: SubscriptionData) => {
      const plan = buildRenewCheckoutPlan(sub, catalog_plans);
      if (!plan) {
        Alert.alert(t('common.error'), t('checkout.missingPlan'));
        return;
      }
      try {
        await setSelectedSubscription(plan);
        navigation.navigate('Checkout', { plan, free_days_selected: 0 });
      } catch {
        Alert.alert(t('common.error'), t('checkout.missingPlan'));
      }
    },
    [catalog_plans, navigation, t],
  );

  const handleUpgrade = useCallback(
    (sub: SubscriptionData) => {
      navigation.navigate('UpgradeCheckout', {
        subscription: sub,
        remainingDays: getSubscriptionUpgradeDayLeft(sub),
      });
    },
    [navigation],
  );

  const outline_footer_cta = useMemo(() => createPageFooterOutlineCtaStyles(themeColors), [themeColors]);

  const handleFreezeTrigger = useCallback((sub: any) => {
    setFreezePopupMode('freeze');
    setFreezePopupSub(sub);
  }, []);

  const handleUnfreezeTrigger = useCallback((sub: any) => {
    const unlockable = filterFreezeToggleableDates(sub?.freeze_dates ?? [], menu_selection_before);
    if (unlockable.length === 0) {
      return;
    }
    setFreezePopupMode('unfreeze');
    setFreezePopupSub(sub);
  }, [menu_selection_before]);

  const handlePayPending = useCallback(
    async (sub: SubscriptionData) => {
      const customer_id = user?.id;
      if (typeof customer_id !== 'number') {
        return;
      }
      if (!default_payment_gateway) {
        Alert.alert(t('common.error'), t('order.paymentInitFailed'));
        return;
      }
      set_pending_action_id(sub.id);
      try {
        const payment_url = await fetchSubscriptionPaymentUrl({
          customer_id,
          customer_subscription_id: sub.id,
          payment_gateway: default_payment_gateway,
        });
        if (payment_url) {
          navigation.navigate('PaymentWebView', {
            url: payment_url,
            subscriptionId: sub.id,
            flow: 'subscription',
          });
        } else {
          Alert.alert(t('common.error'), t('order.failedToGeneratePaymentUrl'));
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : t('order.paymentInitFailed');
        Alert.alert(t('common.error'), message);
      } finally {
        set_pending_action_id(null);
      }
    },
    [user?.id, default_payment_gateway, navigation, t],
  );

  const handleCancelPending = useCallback(
    (sub: SubscriptionData) => {
      Alert.alert(t('order.deleteSubscriptionTitle'), t('order.deleteSubscriptionMessage'), [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('order.delete'),
          style: 'destructive',
          onPress: () => {
            void (async () => {
              set_pending_action_id(sub.id);
              try {
                await cancelPending(sub.id);
                Alert.alert(t('common.success'), t('order.subscriptionRemoved'));
                await Promise.all([refetch?.(), refetch_pending?.()]);
              } catch (error: unknown) {
                const message = error instanceof Error ? error.message : t('order.failedToDeleteSubscription');
                Alert.alert(t('common.error'), message);
              } finally {
                set_pending_action_id(null);
              }
            })();
          },
        },
      ]);
    },
    [cancelPending, refetch, refetch_pending, t],
  );

  if (isLoading) {
    return (
      <View style={[styles.center, { backgroundColor: themeColors.background }]}>
        <MainLoader size={48} color={themeColors.primary} />
      </View>
    );
  }

  if (error) {
    return (
      <StackScreenShell colors={themeColors}>
        <ScreenHeader title={t('subscriptions.title')} onBack={on_back_exit} scrollY={scrollY} />
        <View style={[styles.center, { flex: 1 }]}>
          <ErrorState
            title={t('subscriptions.failedToLoadPlans')}
            message={t('subscriptions.tryAgain')}
            onRetry={refetch}
            error={error}
            issue_context={{
              page: t('account.mySubscriptions'),
              issue: 'Failed to load subscriptions',
              kind: 'api',
              api_endpoint: 'customer_subscriptions',
              http_method: 'GET',
            }}
          />
        </View>
      </StackScreenShell>
    );
  }

  return (
    <StackScreenShell colors={themeColors}>
      <View style={styles.shell}>
        <ScreenHeader title={t('subscriptions.title')} onBack={on_back_exit} scrollY={scrollY} />

        <Animated.ScrollView
          style={styles.scroll}
          onScroll={onScroll}
          scrollEventThrottle={16}
          contentContainerStyle={[styles.scrollContent, { paddingTop: content_top, paddingBottom: scroll_padding_bottom }]}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => {
                setRefreshing(true);
                refetch?.().finally(() => setRefreshing(false));
              }}
              tintColor={themeColors.primary}
              colors={[themeColors.primary]}
              progressBackgroundColor={themeColors.surfaceLight}
            />
          }>
          {display_subscriptions.length === 0 ? (
            <View style={styles.emptyState}>
              <View style={styles.emptyIconWrap}>
                <SvgIcon name="menu" size={48} color={themeColors.primary} />
              </View>
              <Text style={styles.emptyTitle}>{t('subscriptions.noActivePlans')}</Text>
              <Text style={styles.emptySubtitle}>{t('subscriptions.subscribeToStart')}</Text>
            </View>
          ) : (
            display_subscriptions.map((sub, index) => {
              const progress = getMealProgress(sub);
              const payment_status = (sub as { payment_status?: string }).payment_status;
              const is_pending =
                resolveSubscriptionDisplayStatus(sub.status, payment_status) === 'pending';
              return (
                <MySubscriptionCard
                  key={sub.id}
                  index={index}
                  subscription={sub}
                  days_left={progress.daysLeft}
                  paused_days_left={progress.pausedDaysLeft}
                  days_elapsed={progress.daysElapsed}
                  total_days={progress.totalDays}
                  progress_percentage={progress.progressPercentage}
                  onFreeze={() => handleFreezeTrigger(sub)}
                  onUnfreeze={() => handleUnfreezeTrigger(sub)}
                  onRenew={() => void handleRenew(sub)}
                  onUpgrade={() => handleUpgrade(sub)}
                  onPayPending={is_pending ? () => void handlePayPending(sub) : undefined}
                  onCancelPending={is_pending ? () => handleCancelPending(sub) : undefined}
                  pending_action_loading={pending_action_id === sub.id}
                  themeColors={themeColors}
                />
              );
            })
          )}
        </Animated.ScrollView>

        <View style={[styles.pageFooter, { paddingBottom: FOOTER_VERTICAL_PAD + insets.bottom, minHeight: footer_total_height }]}>
          <View style={styles.pageFooterCtaWrap}>
            <Clickable
              onPress={goToSubscriptionList}
              accessibilityLabel={t('subscriptions.buyNewSubscription')}
              accessibilityRole="button"
              style={{ width: '100%' }}>
              <View style={outline_footer_cta.wrap}>
                <View style={[outline_footer_cta.inner, androidFlatSurfaceStyle()]}>
                  <SvgIcon name="plus" size={22} color={themeColors.primary} />
                  <Text style={outline_footer_cta.text}>{t('subscriptions.buyNewSubscription')}</Text>
                </View>
              </View>
            </Clickable>
          </View>
        </View>

        <FreezeCalendarPopup
          visible={freezePopupSub != null}
          onClose={refresh_needed => {
            setFreezePopupSub(null);
            if (refresh_needed) {
              refetch?.();
            }
          }}
          mode={freezePopupMode}
          subscription={freezePopupSub}
          availableDates={freezePopupSub ? filterDatesBySubscription(availableDates, freezePopupSub.id) : []}
          freezeDates={freezePopupSub?.freeze_dates ?? []}
          customerId={user?.id ?? 0}
        />
      </View>
    </StackScreenShell>
  );
};

export default MySubscriptionsScreen;
